// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { observer } from 'mobx-react';

import container from '@core/di';
import Config from '@core/config';
import { SortingType, ActivityFilter } from '@shared/constants/services';
import CustomersStore from '@shared/stores/customers';
import Field from '@shared/components/Form/components/Field';
import Image from '@shared/components/Image';
import Autocomplete, { normalize } from '@shared/components/Autocomplete';
import TextField from '@shared/components/TextField';
import { ASSET_CONSTRUCTION_TYPE_LABELS, TOWER_FIELDS } from '@shared/constants/asset';
import Select, { getOptionsFromLabels } from '@shared/components/Select';
import { CustomerDTO } from '@shared/models/customers';
import { CUSTOMER_FIELDS } from '@shared/constants/customer';
import Switch from '@shared/components/Switch';

export interface TowerProps {
  className?: string;
  form;
  selectedCustomerName?: CustomerDTO['name'];
}

const assetConstructionTypes = getOptionsFromLabels(ASSET_CONSTRUCTION_TYPE_LABELS);

@observer
class Tower extends React.Component<TowerProps> {
  private customersStore = container.get<CustomersStore>(CustomersStore.diToken);
  private config = container.get<Config>(Config.diToken);

  private handleInputChange = (searchKeyword: string) => {
    this.customersStore.getList({
      pagination: { page: 0, pageSize: this.config.get().autocomplete.itemsCount },
      sorting: { type: SortingType.asc, field: CUSTOMER_FIELDS.name },
      filtering: { searchKeyword, activityFilter: ActivityFilter.all, isBlocked: false }
    });
  }

  // private getOptions() {
  //   return normalize(this.customersStore.list);
  // }

  // private getLoadingList() {
  //   return this.customersStore.loading.list;
  // }

  private handleClearOptions() {
    this.customersStore.resetList();
  }

  private get customer() {
    const { selectedCustomerName, form } = this.props;

    if (selectedCustomerName) {
      return selectedCustomerName;
    }

    return (
      <Autocomplete
        disableClearable
        filterSelectedOptions
        showSelectedValues
        showInputValue
        multiple={false}
        placeholder="Search customer"
        field={form.$(TOWER_FIELDS.customerId)}
        // options={this.getOptions.bind(this)}
        // loading={this.getLoadingList.bind(this)}
        options={normalize(this.customersStore.list)}
        loading={this.customersStore.loading.list}
        clearOptions={this.handleClearOptions.bind(this)}
        onInputChange={this.handleInputChange.bind(this)}
        testAttributeArgs={{
          module: 'Asset-management-tower',
          elementDescription: 'customer',
        }}
      />
    );
  }

  private get fields() {
    const { form } = this.props;
    const classes = withStyles.getClasses(this.props);

    return [
      {
        name: TOWER_FIELDS.imageInfo.fields.image,
        label: 'Construction photo',
        component: (
          <Image
            withUpload
            // withCrop
            field={form.$(TOWER_FIELDS.imageInfo.name).$(TOWER_FIELDS.imageInfo.fields.image)}
            classes={{
              imgWrapper: classes.assetImgWrapper,
              root: classes.assetImgRoot
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.name,
        label: 'Name',
        component: (
          <TextField
            fullWidth
            placeholder="Enter asset name"
            margin="none"
            field={form.$(TOWER_FIELDS.name)}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'name',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.construction.fields.type,
        label: 'Type',
        component: (
          <Select
            fullWidth
            placeholder="Select type"
            options={assetConstructionTypes}
            margin="none"
            field={form.$(TOWER_FIELDS.construction.name).$(TOWER_FIELDS.construction.fields.type)}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'type',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.serialNumber,
        label: 'Serial number',
        component: (
          <TextField
            fullWidth
            placeholder="xxxx xxxx xxxx xxxx"
            margin="none"
            field={form.$(TOWER_FIELDS.serialNumber)}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'serial-number',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.customerId,
        label: 'Customer',
        component: this.customer
      },
      {
        name: TOWER_FIELDS.note,
        label: 'Notes',
        component: (
          <TextField
            multiline
            fullWidth
            placeholder="Enter note"
            margin="none"
            field={form.$(TOWER_FIELDS.note)}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'notes',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.address.name,
        label: 'Address',
        classes: { label: classes.address }
      },
      {
        name: TOWER_FIELDS.address.fields.addressLine,
        label: 'Street number and name',
        component: (
          <TextField
            fullWidth
            placeholder="Enter street number and name"
            margin="none"
            field={form.$(TOWER_FIELDS.address.name).$(TOWER_FIELDS.address.fields.addressLine)}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'address-line',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.address.fields.city,
        label: 'City',
        component: (
          <TextField
            fullWidth
            placeholder="Enter city"
            margin="none"
            field={form.$(TOWER_FIELDS.address.name).$(TOWER_FIELDS.address.fields.city)}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'address-city',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.address.fields.state,
        label: 'State',
        component: (
          <TextField
            fullWidth
            placeholder="Enter state"
            margin="none"
            field={form.$(TOWER_FIELDS.address.name).$(TOWER_FIELDS.address.fields.state)}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'address-state',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.address.fields.zipCode,
        label: 'ZIP code',
        component: (
          <TextField
            // fullWidth
            placeholder="ZIP code"
            margin="none"
            field={form.$(TOWER_FIELDS.address.name).$(TOWER_FIELDS.address.fields.zipCode)}
            className={classes.zipCodeField}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'address-zip-code',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.address.fields.country,
        label: 'Country',
        component: (
          <TextField
            // fullWidth
            placeholder="Country"
            margin="none"
            field={form.$(TOWER_FIELDS.address.name).$(TOWER_FIELDS.address.fields.country)}
            className={classes.countryField}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'address-country',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.construction.fields.height,
        label: 'Height, m',
        component: (
          <TextField
            // fullWidth
            placeholder="Enter height"
            margin="none"
            field={form.$(TOWER_FIELDS.construction.name).$(TOWER_FIELDS.construction.fields.height)}
            className={classes.numericField}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'height',
            }}
          />
        )
      },
      {
        name: TOWER_FIELDS.coordinates.name,
        label: 'Lat & long',
        component: (
          <div className={classes.coordinates}>
            <TextField
              // fullWidth
              placeholder="lat"
              margin="none"
              field={form.$(TOWER_FIELDS.coordinates.name).$(TOWER_FIELDS.coordinates.fields.lat)}
              className={classes.numericField}
              testAttributeArgs={{
                module: 'Asset-management-tower',
                elementDescription: 'lat',
              }}
            />
            <TextField
              // fullWidth
              placeholder="long"
              margin="none"
              field={form.$(TOWER_FIELDS.coordinates.name).$(TOWER_FIELDS.coordinates.fields.lon)}
              className={classes.numericField}
              testAttributeArgs={{
                module: 'Asset-management-tower',
                elementDescription: 'lon',
              }}
            />
          </div>
        )
      },
      {
        name: TOWER_FIELDS.isBlocked,
        label: 'Disable',
        shouldRender: form.fields.has(TOWER_FIELDS.isBlocked),
        component: form.fields.has(TOWER_FIELDS.isBlocked) && (
          <Switch
            field={form.$(TOWER_FIELDS.isBlocked)}
            testAttributeArgs={{
              module: 'Asset-management-tower',
              elementDescription: 'deactivation'
            }}
          />
        )
      }
    ].filter(({ shouldRender = true }) => shouldRender);
  }

  render() {
    const classes = withStyles.getClasses(this.props);
    
    return this.fields.map(({ name, label, component }) => (
      <Field
        label={label}
        component={component}
        key={name}
        classes={{ label: classes.fieldLabel, component: classes.fieldComponent, root: classes.fieldRoot }}
      />
    ));
  }
}

const TowerStyled = withStyles(
  Tower, 
  (theme, props) => ({
    "root": {},
    "coordinates": {
      display: 'flex',
      gap: theme.spacing(8)
    },
    "zipCodeField": {
      width: 132
    },
    "countryField": {
      width: 132
    },
    "numericField": {
      marginRight: theme.spacing(4),
      width: 128
    },
    "address": {
      fontSize: 17,
      color: theme.palette.colors.primary
    },
    "selectMenu": {
      width: 290,
    },
    "assetImgWrapper": {
      alignItems: 'flex-start'
    },
    "assetImgRoot": {
      width: 'auto',
      maxWidth: 'calc(100% - 32px)',
      objectFit: 'contain',
    },
    "fieldRoot": {},
    "fieldLabel": {},
    "fieldComponent": {},
  })
);

export default TowerStyled;